import React, { createContext, useContext, useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  compilePattern,
  compilePatternToText,
} from "../services/patternCompiler";
import localforage from "localforage";

const PatternContext = createContext();

export const usePatterns = () => {
  const context = useContext(PatternContext);
  if (!context) {
    throw new Error("usePatterns must be used within a PatternProvider");
  }
  return context;
};

export const PatternProvider = ({ children }) => {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPattern, setCurrentPattern] = useState(null);

  // Load patterns from localStorage
  useEffect(() => {
    const loadPatterns = async () => {
      try {
        const savedPatterns = await localforage.getItem("patterns");
        if (savedPatterns) {
          setPatterns(savedPatterns);
        }
      } catch (error) {
        console.error("Error loading patterns:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPatterns();
  }, []);

  // Save patterns to localStorage whenever patterns change
  useEffect(() => {
    if (!loading) {
      localforage.setItem("patterns", patterns);
    }
  }, [patterns, loading]);

  const createPattern = (patternData) => {
    const newPattern = {
      id: uuidv4(),
      title: patternData.title || "Untitled Pattern",
      chart: patternData.chart,
      paragraph: compilePatternToText(compilePattern(patternData.chart)),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      syncedToDrive: false,
    };

    setPatterns((prev) => [newPattern, ...prev]);
    return newPattern;
  };

  const updatePattern = (id, updates) => {
    setPatterns((prev) =>
      prev.map((pattern) =>
        pattern.id === id
          ? {
              ...pattern,
              ...updates,
              updatedAt: new Date().toISOString(),
              paragraph: updates.chart
                ? compilePatternToText(compilePattern(updates.chart))
                : pattern.paragraph,
            }
          : pattern,
      ),
    );
  };

  const deletePattern = (id) => {
    setPatterns((prev) => prev.filter((pattern) => pattern.id !== id));
  };

  const getPattern = (id) => {
    return patterns.find((pattern) => pattern.id === id);
  };

  const duplicatePattern = (id) => {
    const originalPattern = getPattern(id);
    if (!originalPattern) return null;

    const duplicatedPattern = {
      ...originalPattern,
      id: uuidv4(),
      title: `${originalPattern.title} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      syncedToDrive: false,
    };

    setPatterns((prev) => [duplicatedPattern, ...prev]);
    return duplicatedPattern;
  };

  const exportPattern = (id, format) => {
    const pattern = getPattern(id);
    if (!pattern) return null;

    return {
      ...pattern,
      format,
      exportedAt: new Date().toISOString(),
    };
  };

  const importPattern = (patternData) => {
    const importedPattern = {
      id: uuidv4(),
      title: patternData.title || "Imported Pattern",
      chart: patternData.chart,
      paragraph:
        patternData.paragraph ||
        compilePatternToText(compilePattern(patternData.chart)),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      syncedToDrive: false,
    };

    setPatterns((prev) => [importedPattern, ...prev]);
    return importedPattern;
  };

  const value = {
    patterns,
    loading,
    currentPattern,
    setCurrentPattern,
    createPattern,
    updatePattern,
    deletePattern,
    getPattern,
    duplicatePattern,
    exportPattern,
    importPattern,
  };

  return (
    <PatternContext.Provider value={value}>{children}</PatternContext.Provider>
  );
};
